
import React, { useEffect, useRef, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { BENEFITS } from '../constants';
import { useLanguage } from '../context/LanguageContext';

const WhyChooseMe: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { t, isUrdu } = useLanguage();

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || window.innerWidth >= 1024) return;

    const interval = setInterval(() => {
      if (!isPaused) {
        const nextIndex = (activeIndex + 1) % BENEFITS.length;
        const scrollAmount = scrollContainer.offsetWidth * nextIndex;
        
        scrollContainer.scrollTo({
          left: scrollAmount,
          behavior: 'smooth'
        });
        setActiveIndex(nextIndex);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [activeIndex, isPaused]);

  const handleManualScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const index = Math.round(container.scrollLeft / container.offsetWidth);
    if (index !== activeIndex) setActiveIndex(index);
  };

  return (
    <section 
      id="why-choose-me" 
      aria-label="Why Partner With Taibe Javed - Key Value Propositions"
      className="py-14 sm:py-20 bg-slate-50 overflow-hidden relative"
    >
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-block px-4 py-1.5 bg-white border border-slate-200 rounded-full text-indigo-600 text-[10px] font-bold uppercase tracking-[0.3em] mb-6 shadow-sm">
            {t.whyChooseMe.badge}
          </div>
          <h2 className="text-3xl sm:text-5xl font-display font-extrabold text-slate-900 mb-6 tracking-tight">
            {t.whyChooseMe.title}
          </h2>
          <p className="max-w-xl mx-auto text-slate-600 text-sm sm:text-base font-normal">
            {t.whyChooseMe.subtitle}
          </p>
          <div className="mt-8 max-w-3xl mx-auto bg-white p-6 rounded-2xl border border-slate-200 text-center shadow-sm">
            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
              {t.whyChooseMe.description}
            </p>
          </div>
        </div>

        {/* Enhanced Responsive Layout: Slider on Mobile, Grid on Desktop */}
        <div className="relative">
          <div 
            ref={scrollRef}
            onScroll={handleManualScroll}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
            className="flex lg:grid lg:grid-cols-4 gap-6 sm:gap-8 overflow-x-auto lg:overflow-visible scrollbar-hide snap-x snap-mandatory"
          >
            {BENEFITS.map((benefit, index) => {
              const IconComponent = (LucideIcons as any)[benefit.iconName];
              
              return (
                <div 
                  key={index} 
                  className="group relative min-w-[100%] sm:min-w-[45%] lg:min-w-0 snap-center p-8 sm:p-10 bg-white rounded-[2rem] border border-slate-200 transition-all duration-300 hover:border-indigo-300 hover:shadow-xl flex flex-col items-center text-center shadow-sm"
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mb-6 transition-all duration-300 group-hover:scale-105 shadow-sm">
                    {IconComponent && <IconComponent size={32} />}
                  </div>
                  
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 tracking-tight">
                    {benefit.title}
                  </h3>
                  
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-normal">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Mobile Navigation Dots */}
          <div className="flex lg:hidden justify-center gap-3 mt-10" role="tablist" aria-label="Benefit Carousel Navigation">
            {BENEFITS.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-label={`Slide ${i + 1}`}
                aria-selected={activeIndex === i}
                onClick={() => {
                  const scrollAmount = scrollRef.current!.offsetWidth * i;
                  scrollRef.current!.scrollTo({ left: scrollAmount, behavior: 'smooth' });
                  setActiveIndex(i);
                }}
                className={`h-1.5 transition-all duration-300 rounded-full cursor-pointer ${
                  activeIndex === i ? 'w-10 bg-indigo-600 shadow-sm' : 'w-2 bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Full-Width High-Impact Banner */}
      <div className="mt-20 sm:mt-28 w-full bg-slate-950 text-white relative overflow-hidden py-16 sm:py-24 border-y border-indigo-900/40">
        {/* Ambient Glowing Background Elements */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-indigo-600/20 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-blue-600/15 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.07] pointer-events-none" />

        <div className="w-full px-6 sm:px-12 lg:px-20 xl:px-28 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-16">
            
            {/* Left Column: Heading & Copy */}
            <div className="space-y-6 text-center lg:text-left max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-indigo-950/80 border border-indigo-800/60 rounded-full shadow-inner">
                <LucideIcons.Sparkles size={14} className="text-indigo-400 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-300">
                  {isUrdu ? 'اعلی اثر انگیز ڈیجیٹل پارٹنر' : 'High-Impact Digital Partner'}
                </span>
              </div>

              <h3 className="text-3xl sm:text-5xl lg:text-6xl font-display font-black leading-[1.1] tracking-tight">
                {isUrdu ? 'پیشہ ورانہ معیار کے لیے تیار ہیں؟' : 'Ready for Professional Standards?'}
              </h3>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl font-normal">
                {isUrdu 
                  ? 'سست اور عام ٹیمپلیٹس پر سمجھوتہ بند کریں۔ اپنی مرضی کا ویب سلوشن اور دلکش AI ویڈیو مواد حاصل کریں۔'
                  : 'Stop settling for slow, generic templates. Get a custom-engineered web solution & high-engagement AI video media designed for maximum conversions and authority.'}
              </p>

              {/* Value Pillar Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2">
                {[
                  { icon: LucideIcons.Zap, text: isUrdu ? 'کسٹم ورڈپریس اور کوڈ' : 'Custom WordPress & Code' },
                  { icon: LucideIcons.ShieldCheck, text: isUrdu ? 'صاف اور محفوظ آرکیٹیکچر' : 'Clean & Secure Architecture' },
                  { icon: LucideIcons.TrendingUp, text: isUrdu ? 'SEO اور اسپیڈ آپٹیمائزڈ' : 'SEO & Speed Optimized' }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-center gap-2 bg-slate-900/90 border border-indigo-900/50 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-300">
                      <Icon size={14} className="text-indigo-400" />
                      <span>{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: CTA Buttons */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-4 w-full sm:w-auto flex-shrink-0 items-center justify-center">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                aria-label="Start elite deployment protocol"
                className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white px-9 py-5 rounded-2xl font-extrabold text-xs sm:text-sm uppercase tracking-[0.2em] transition-all duration-300 shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:shadow-[0_0_40px_rgba(99,102,241,0.6)] active:scale-95 cursor-pointer"
              >
                <span>{isUrdu ? 'پروجیکٹ شروع کریں' : 'Start Deployment'}</span>
                <LucideIcons.ArrowRight size={18} className={`transition-transform ${isUrdu ? 'group-hover:-translate-x-1.5 rotate-180' : 'group-hover:translate-x-1.5'}`} aria-hidden="true" />
              </button>

              <button 
                onClick={() => document.getElementById('estimator')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 px-7 py-4 rounded-2xl font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer"
              >
                <LucideIcons.Calculator size={15} className="text-indigo-400" />
                <span>{isUrdu ? 'لاگت کا تخمینہ لگائیں' : 'Calculate Cost Estimate'}</span>
              </button>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseMe;
